/**
 * @file UpdateGroupModal.js
 * @author Paola Bustos, Yevheniia Bazhmaieva, Simon Tenedero
 * @created 2025-07-02
 * @lastModified 2025-07-28
 * @description file containing UpdateGroupModal component
 */

import React, { useState } from "react";
import {
  updateRoleCloud,
  AVAILABLE_FEATURES,
  deleteRoleCloud,
} from "../../utils/roleUtils.js";

/**
 * This component is a modal (popup) that allows users to update an existing user group (this is used in the admin panel).
 *
 * @param {Object} props - The component properties
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onGroupCreated - Callback function to call when group is successfully created
 * @param {Object} props.groupData - Current group data to pre-fill the form
 *
 * @returns {JSX.Element} The rendered UpdateGroupModal component
 */
const UpdateGroupModal = ({ isOpen, onClose, onGroupCreated, groupData }) => {
  /*hooks must always be declared at the top*/
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [formData, setFormData] = useState({
    roleName: "",
    description: "",
    features: {},
  });

  // Pre-fill form with group data when modal opens
  React.useEffect(() => {
    if (isOpen && groupData) {
      // Ensure all available features are present in the form state
      const initialFeatures = {};
      Object.values(AVAILABLE_FEATURES).forEach((feature) => {
        initialFeatures[feature] = groupData.features?.[feature] || false;
      });

      setFormData({
        roleName: groupData.roleName || "",
        description: groupData.description || "",
        features: initialFeatures,
      });
    } else if (!isOpen) {
      // Reset form and confirmation state when modal closes
      setFormData({
        roleName: "",
        description: "",
        features: {},
      });
      setError("");
      setIsConfirmingDelete(false);
    }
  }, [isOpen, groupData]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle feature checkbox changes with hierarchy logic
  const handleFeatureChange = (featureKey, checked) => {
    setFormData((prev) => {
      const newFeatures = { ...prev.features };
      newFeatures[featureKey] = checked;

      // If livestream_panel is disabled, disable its children and grandchildren
      if (featureKey === "livestream_panel" && !checked) {
        newFeatures["youtube_chat"] = false;
        newFeatures["private_chat"] = false;
        newFeatures["native_chat"] = false;
        newFeatures["AI_summary"] = false; // Grandchild
      }

      // If youtube_chat is disabled, disable its child
      if (featureKey === "youtube_chat" && !checked) {
        newFeatures["AI_summary"] = false;
      }

      return {
        ...prev,
        features: newFeatures,
      };
    });
  };

  // Convert feature keys to readable labels
  const getFeatureLabel = (featureKey) => {
    return featureKey
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Validate form
  const validateForm = () => {
    if (!formData.roleName.trim()) {
      setError("Group name is required");
      return false;
    }

    if (formData.roleName.length < 2) {
      setError("Group name must be at least 2 characters");
      return false;
    }

    if (formData.roleName.includes(" ")) {
      setError(
        "Group name cannot contain spaces (use camelCase or underscores)",
      );
      return false;
    }

    if (!formData.description.trim()) {
      setError("Description is required");
      return false;
    }

    const hasAnyFeature = Object.values(formData.features).some(
      (feature) => feature === true,
    );
    if (!hasAnyFeature) {
      setError("Please select at least one feature");
      return false;
    }

    setError("");
    return true;
  };

  // Handle delete operation
  const handleDelete = async () => {
    if (!groupData?.id) {
      setError("Cannot delete group: ID is missing.");
      return;
    }

    if (
      groupData.roleName === "admin" ||
      groupData.roleName === "unrecognized"
    ) {
      setError("This default group cannot be deleted.");
      setIsConfirmingDelete(false);
      return;
    }

    setLoading(true);
    try {
      await deleteRoleCloud(groupData.id);
      onClose(); // Close modal on success
      onGroupCreated && onGroupCreated(); // Refresh the list
    } catch (err) {
      console.error("Error deleting group:", err);
      setError(err.message || "Failed to delete group.");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      groupData.roleName === "admin" ||
      groupData.roleName === "unrecognized"
    ) {
      setError("This default group cannot be updated.");
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    try {
      await updateRoleCloud(groupData.id, formData);
      onGroupCreated && onGroupCreated(); // Refresh the list
      onClose(); // Close modal on success
    } catch (error) {
      console.error("Error updating group:", error);
      setError(error.message || "Failed to update group. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Define the feature order for hierarchical display
  const featureOrder = [
    "admin_panel",
    "archive_chat",
    "livestream_panel", // Parent
    "youtube_chat", // Child of livestream_panel
    "AI_summary", // Grandchild (child of youtube_chat)
    "private_chat", // Child of livestream_panel
    "native_chat", // Child of livestream_panel
    "settings",
    "profile",
  ];

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-base-200 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-scroll">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-2xl font-bold text-base-content">
                Update Group
              </h2>
              <p className="text-base-content/70">
                Modify permissions and features for '{groupData.roleName}'
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            disabled={loading}
            className="btn btn-ghost btn-sm btn-circle"
          >
            <span className="text-lg">×</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          {/* Group Name */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Group Name</span>
            </label>
            <input
              type="text"
              name="roleName"
              value={formData.roleName}
              onChange={handleInputChange}
              placeholder="e.g., experimentGroup1, moderators"
              className="input input-bordered w-full"
              disabled={loading}
              maxLength={50}
            />
            <label className="label">
              <span className="label-text-alt text-warning">
                Warning: Changing the name is a costly operation.
              </span>
            </label>
          </div>

          {/* Description */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Description</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="e.g., Experimental group for testing new chat features"
              className="textarea textarea-bordered h-24 resize-none"
              disabled={loading}
              maxLength={200}
            />
            <label className="label">
              <span className="label-text-alt">
                {formData.description.length}/200 characters
              </span>
            </label>
          </div>

          {/* Features Selection */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">
                Features & Permissions
              </span>
            </label>

            <div className="bg-base-100 rounded-lg p-4 space-y-3">
              {featureOrder.map((featureKey) => {
                // Find the actual feature value from AVAILABLE_FEATURES
                const value =
                  Object.values(AVAILABLE_FEATURES).find(
                    (v) => v === featureKey,
                  ) || featureKey;

                // Visibility logic for hierarchy
                if (
                  (value === "youtube_chat" ||
                    value === "private_chat" ||
                    value === "native_chat") &&
                  !formData.features["livestream_panel"]
                ) {
                  return null;
                }

                if (
                  value === "AI_summary" &&
                  !formData.features["youtube_chat"]
                ) {
                  return null;
                }

                // Determine indentation and style
                let className =
                  "flex items-center justify-between p-3 bg-base-200 rounded-lg hover:bg-base-300 transition-colors";
                let prefix = "";

                if (
                  value === "youtube_chat" ||
                  value === "private_chat" ||
                  value === "native_chat"
                ) {
                  className += " ml-6 border-l-4 border-primary";
                  prefix = "↳ ";
                }

                if (value === "AI_summary") {
                  className += " ml-12 border-l-4 border-secondary";
                  prefix = "↳ ";
                }

                return (
                  <div key={value} className={className}>
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium text-base-content">
                          {prefix}
                          {getFeatureLabel(value)}
                        </div>
                        {/* Feature Descriptions */}
                        <div className="text-sm text-base-content/70">
                          {value === "livestream_panel" &&
                            "Access to livestream features and chat systems"}
                          {value === "youtube_chat" &&
                            "Allow viewing and participating in YouTube chat"}
                          {value === "AI_summary" &&
                            "View AI summary for YouTube chat"}
                          {value === "native_chat" &&
                            "Access to native chat system"}
                          {value === "private_chat" &&
                            "Create and join private chat rooms"}
                          {value === "admin_panel" &&
                            "Full administrative access and controls"}
                          {value === "archive_chat" &&
                            "View and manage chat archives"}
                          {value === "settings" &&
                            "Access to user settings and preferences"}
                          {value === "profile" &&
                            "Manage user profile and information"}
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={formData.features[value] || false}
                      onChange={(e) =>
                        handleFeatureChange(value, e.target.checked)
                      }
                      className="checkbox checkbox-primary"
                      disabled={loading}
                    />
                  </div>
                );
              })}
            </div>

            <label className="label">
              <span className="label-text-alt">
                Selected:{" "}
                {Object.values(formData.features).filter(Boolean).length} /{" "}
                {Object.keys(AVAILABLE_FEATURES).length} features
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-base-300">
            {/* Delete button / confirmation */}
            <div>
              {!isConfirmingDelete ? (
                <button
                  type="button"
                  onClick={() => setIsConfirmingDelete(true)}
                  disabled={loading}
                  className="btn btn-error btn-outline"
                >
                  Delete Group
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-error font-semibold">
                    Are you sure?
                  </span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="btn btn-error btn-sm"
                  >
                    Confirm Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsConfirmingDelete(false)}
                    disabled={loading}
                    className="btn btn-ghost btn-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Cancel and Update buttons on the right */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="btn btn-ghost"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary gap-2"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Updating...
                  </>
                ) : (
                  "Update Group"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateGroupModal;
